import React, { useEffect, useState } from 'react';
import ScoreGauge, { CIRCLE_R, CIRCLE_CIRCUMFERENCE } from './ScoreGauge';
import { scoreColor, safetyScoreColor, affordabilityScoreColor, livabilityScoreColor } from '../utils/scoring';

/** Rounded average of available scores (1–10), or null if none. */
function locationRatingAverage(
  safetyScore,
  affordabilityScore,
  livabilityScore,
  includeSafety = true,
  includeAffordability = true,
  includeLivability = true
) {
  const values = [
    includeSafety ? safetyScore : null,
    includeAffordability ? affordabilityScore : null,
    includeLivability ? livabilityScore : null,
  ].filter((s) => s != null && !Number.isNaN(s) && s > 0);
  if (values.length === 0) return null;
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
}

/** Location Rating (main circle = rounded average of safety + affordability + livability) + tooltip; beside it small gauges. */
export default function LocationRatingBlock({
  safetyScore,
  affordabilityScore,
  livabilityScore,
  livabilityBreakdown,
  contextLabel,
  hideSafety = false,
  hideAffordability = false,
  hideLivability = false,
}) {
  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const average = locationRatingAverage(
    safetyScore,
    affordabilityScore,
    livabilityScore,
    !hideSafety,
    !hideAffordability,
    !hideLivability
  );
  const [displayAverage, setDisplayAverage] = useState(0);
  useEffect(() => {
    const t = requestAnimationFrame(() => {
      setDisplayAverage(average != null ? average : 0);
    });
    return () => cancelAnimationFrame(t);
  }, [average]);
  const mainOffset = CIRCLE_CIRCUMFERENCE * (1 - displayAverage / 10);
  const mainColor = average != null ? scoreColor(Math.round(average)) : 'var(--color-border)';
  const tooltipParts = [];
  if (!hideSafety && safetyScore != null) tooltipParts.push(`Safety: ${safetyScore}/10`);
  if (!hideAffordability && affordabilityScore != null) tooltipParts.push(`Affordability: ${affordabilityScore}/10`);
  if (!hideLivability && livabilityScore != null) tooltipParts.push(`Livability: ${livabilityScore}/10`);
  const showMainTooltip = activeTooltipId === 'main';
  const handleMainPointerEnter = (e) => { if (e.pointerType === 'mouse') setActiveTooltipId('main'); };
  const handleMainPointerLeave = (e) => { if (e.pointerType === 'mouse') setActiveTooltipId(null); };
  const handleMainClick = () => setActiveTooltipId(showMainTooltip ? null : 'main');
  const handleLivabilityToggle = (id) => setActiveTooltipId(id);
  return (
    <div className="search-page__location-rating">
      <div
        className="search-page__location-rating__main-wrap"
        onPointerEnter={handleMainPointerEnter}
        onPointerLeave={handleMainPointerLeave}
        onFocus={() => setActiveTooltipId('main')}
        onBlur={() => setActiveTooltipId(null)}
        onClick={handleMainClick}
      >
        <div className="search-page__location-rating__main" aria-describedby={showMainTooltip ? `location-rating-tt-${contextLabel}` : undefined}>
          <svg viewBox="0 0 100 100" className="search-page__location-rating__ring" aria-hidden="true">
            <circle cx="50" cy="50" r={CIRCLE_R} fill="none" className="search-page__safety-gauge__track" strokeWidth="8" />
            <circle
              cx="50"
              cy="50"
              r={CIRCLE_R}
              fill="none"
              className="search-page__safety-gauge__fill"
              strokeWidth="8"
              strokeLinecap="round"
              stroke={mainColor}
              strokeDasharray={CIRCLE_CIRCUMFERENCE}
              strokeDashoffset={mainOffset}
              transform="rotate(-90 50 50)"
            />
          </svg>
          <span className="search-page__location-rating__value">{average != null ? average : '—'}</span>
        </div>
        <span className="search-page__location-rating__label">Location Rating</span>
        {showMainTooltip && tooltipParts.length > 0 && (
          <div
            id={`location-rating-tt-${contextLabel}`}
            className="search-page__location-rating__tooltip"
            role="tooltip"
          >
            {tooltipParts.map((p, i) => <div key={i}>{p}</div>)}
          </div>
        )}
      </div>
      <div className="search-page__location-rating__gauges">
        {!hideSafety && safetyScore != null && (
          <ScoreGauge score={safetyScore} label="Safety" size="small" colorFn={safetyScoreColor} />
        )}
        {!hideAffordability && affordabilityScore != null && (
          <ScoreGauge
            score={affordabilityScore}
            label="Cost"
            size="small"
            colorFn={affordabilityScoreColor}
            gaugeClass="search-page__affordability-gauge"
            ariaLabel={`Affordability ${affordabilityScore} out of 10`}
          />
        )}
        {!hideLivability && livabilityScore != null && (
          <ScoreGauge
            score={livabilityScore}
            label="Livability"
            size="small"
            colorFn={livabilityScoreColor}
            gaugeClass="search-page__livability-gauge"
            breakdown={livabilityBreakdown}
            showTooltip={activeTooltipId === 'livability'}
            onTooltipToggle={handleLivabilityToggle}
          />
        )}
      </div>
    </div>
  );
}
